import { type GetStaticPathsResult, type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { collectionPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCollectionPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
  getPageUrl,
} from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getCollectionPage,
} from '@lib/sanity/page'
import { getBreadcrumbListSchema } from '@lib/schema/breadcrumb'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CollectionPageProps = BasePageProps & {
  slug: string
  page: SanityCollectionPage
  site: SanitySiteFragment
}

type StaticCollectionPageParams = ParsedUrlQuery & {
  slug: string
}

const CollectionPage = ({
  draftMode,
  locale,
  slug,
  page,
  site,
}: CollectionPageProps) => {
  const canonicalUrl = useMemo(() => {
    const domain = site.generalSettings.siteURL.replace(/\/+$/g, '')
    const pageUrl = getPageUrl(locale, 'shopPageChild', slug)

    return `${domain}${pageUrl}`
  }, [locale, site, slug])

  const schemas = useMemo(() => {
    const organizationUrl = site.schemaSettings?.organization?.url
    const shopPageUrl = getPageUrl(locale, 'shopPage')
    const collectionPageUrl = getPageUrl(locale, 'collection', slug)
    const breadcrumbListSchema = getBreadcrumbListSchema([
      {
        title: page.shopPage?.seo?.metaTitle,
        url: `${organizationUrl}${shopPageUrl}`,
      },
      {
        title: page.seo?.metaTitle ?? page.title,
        url: `${organizationUrl}${collectionPageUrl}`,
      },
    ])
    const videoObjectSchemas = getVideoObjectSchemas(page)

    return [breadcrumbListSchema, ...videoObjectSchemas]
  }, [locale, page, site, slug])

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && (
        <Modules
          modules={page.modules}
          quickFilterBasePath={`/shop/${slug}`}
          quickFilterAllPath={`/shop/${slug}`}
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={collectionPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            collectionSlug: slug,
          }}
          quickFilterBasePath={`/shop/${slug}`}
          quickFilterAllPath={`/shop/${slug}`}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticCollectionPageParams>,
): Promise<GetStaticPropsResult<CollectionPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const collectionPage = await getCollectionPage(
    sanityClient,
    context.locale,
    slug,
  )

  if (!collectionPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      page: getNonNullValues(collectionPage.page),
      site: getNonNullValues(collectionPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCollectionPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allCollections = await getAllSlugs(sanityClient, 'collection')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allCollections
    .map((collection) => ({
      ...collection,
      slug: collection.slug.current,
      locale: collection.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slug,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionPage
